import React from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/tr';
import { KTSVG } from 'src/_metronic/helpers';
import { useConversations } from 'src/app/pages/messages/services/chatHooks';
import { useChatSocket } from 'src/app/pages/messages/context/useChatSocket';
import { ChatConversation } from 'src/app/pages/messages/types';

/**
 * Mesajlar Önizleme Menüsü
 * Topbar'daki mesaj butonuna tıklanınca son 5 sohbeti (son mesaj önizlemesiyle) gösterir.
 * WorkflowSummaryMenu (Bekleyen Görevler) ile aynı desende.
 */
const MessagesSummaryMenu: React.FC = () => {
  const { data, isLoading, error } = useConversations();
  const { myId, openConversation } = useChatSocket();

  const recent = React.useMemo(() => {
    const list = (data || []).filter((c) => !!c.lastMessage);
    return [...list].sort((a, b) => new Date(b.lastMessageAt || 0).getTime() - new Date(a.lastMessageAt || 0).getTime()).slice(0, 5);
  }, [data]);

  const totalUnread = React.useMemo(() => (data || []).reduce((s, c) => s + (c.unreadCount || 0), 0), [data]);

  const titleOf = (c: ChatConversation) => (c.type === 'dm' ? `${c.otherUser?.name || ''} ${c.otherUser?.surname || ''}`.trim() || 'Kullanıcı' : c.name || 'Kanal');

  const previewOf = (c: ChatConversation) => {
    const last = c.lastMessage;
    if (!last) return 'Henüz mesaj yok';
    const prefix = last.senderId === myId ? 'Sen: ' : '';
    const body = last.content || (last.hasAttachment ? '📎 Ek' : '');
    return `${prefix}${body}`;
  };

  return (
    <div className="menu menu-sub menu-sub-dropdown menu-column w-300px w-lg-350px" data-kt-menu="true">
      {/* Header */}
      <div className="d-flex flex-column bgi-no-repeat rounded-top px-9 pt-8 pb-6" style={{ backgroundImage: "url('/media/misc/pattern-1.jpg')" }}>
        <h3 className="text-white fw-bold mb-2 d-flex align-items-center">
          Mesajlar
          {totalUnread > 0 && <span className="badge bg-danger ms-2">{totalUnread > 99 ? '99+' : totalUnread} okunmamış</span>}
        </h3>
        <span className="text-white opacity-75 fs-8">Son mesajların önizlemesi</span>
      </div>

      {/* Liste */}
      <div className="scroll-y mh-350px my-0">
        {isLoading ? (
          <div className="text-center text-muted py-8 fs-7">Yükleniyor…</div>
        ) : error ? (
          <div className="text-center text-danger py-8 fs-7">Mesajlar yüklenemedi</div>
        ) : recent.length === 0 ? (
          <div className="text-center text-muted py-8 fs-7">Henüz mesaj yok</div>
        ) : (
          recent.map((c) => {
            const isDm = c.type === 'dm';
            const initials = isDm ? `${c.otherUser?.name?.[0] || ''}${c.otherUser?.surname?.[0] || ''}` : '#';
            return (
              <Link
                key={c.id}
                to="/messages/chat"
                data-kt-menu-dismiss="true"
                onClick={() => openConversation(c.id)}
                className="d-flex align-items-center px-6 py-4 border-bottom bg-hover-light text-gray-800"
              >
                <div className="symbol symbol-40px symbol-circle me-3">
                  <span className={`symbol-label fs-6 fw-bold ${isDm ? 'bg-light-primary text-primary' : 'bg-light-info text-info'}`}>{initials || '?'}</span>
                </div>
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="fw-bold text-gray-900 text-truncate">{titleOf(c)}</span>
                    <span className="text-muted fs-8 flex-shrink-0 ms-2">{c.lastMessageAt ? moment(c.lastMessageAt).locale('tr').fromNow() : ''}</span>
                  </div>
                  <div className="d-flex align-items-center justify-content-between">
                    <span className={`fs-7 text-truncate ${c.unreadCount > 0 ? 'fw-bold text-gray-800' : 'text-muted'}`}>{previewOf(c)}</span>
                    {c.unreadCount > 0 && <span className="badge badge-circle badge-danger ms-2 flex-shrink-0">{c.unreadCount > 99 ? '99+' : c.unreadCount}</span>}
                  </div>
                </div>
              </Link>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="py-3 text-center border-top">
        <Link to="/messages/chat" data-kt-menu-dismiss="true" className="btn btn-color-gray-600 btn-active-color-primary d-flex align-items-center justify-content-center">
          Tüm mesajlar
          <KTSVG path="/media/icons/duotone/Navigation/Arrow-right.svg" className="svg-icon-5 ms-1" />
        </Link>
      </div>
    </div>
  );
};

export { MessagesSummaryMenu };
